import stringify from 'json-stable-stringify'

import { findCookie } from '../utils/cookies'
import RequestEntity from './RequestEntity'

export default class LumifyResource extends RequestEntity {
  static fetchOptionsPlugin = options => {
    // Backend StatelessCsrfFilter issues cookie 'CSRF-TOKEN' and expects header 'X-CSRF-TOKEN'.
    // Code previously sent header 'X-XSRF-TOKEN', causing CSRF rejection on POST list endpoints.
    // Only attempt legacy 'XSRF-TOKEN' cookie if introduced elsewhere; primary cookie is 'CSRF-TOKEN'.
    const token = findCookie('CSRF-TOKEN') || findCookie('XSRF-TOKEN')

    return {
      ...options,
      // Ensure cookies included in case deployment is cross-subdomain; same-origin default covers most cases.
      credentials: options.credentials || 'same-origin',
      headers: {
        // Backend validates against X-CSRF-TOKEN; include only if we have a token.
        ...(token ? { 'X-CSRF-TOKEN': token } : {}),
        ...options.headers
      }
    }
  }

  static listShape() {
    const url = () => `${this.urlRoot}/list`
    const getFetchKey = params => {
      if (Object.keys(params ?? {}).length) {
        return `${url()}?${stringify(params)}`
      }
      return url()
    }
    const key = params => {
      if (Object.keys(params ?? {}).length) {
        return `${url()}?${stringify(params)}`
      }
      return url()
    }
    const options = this.getFetchOptions()
    const endpoint = (params = {}) => this.fetch('post', url(), params)
    endpoint.type = 'read'
    endpoint.schema = { total: 0, rows: [this] }
    endpoint.options = options
    endpoint.getFetchKey = getFetchKey
    endpoint.key = key
    endpoint.fetch = endpoint
    return endpoint
  }

  static createShape() {
    const getFetchKey = params => {
      if (Object.keys(params ?? {}).length) {
        return `${this.urlRoot}/create?${stringify(params)}`
      }
      return `${this.urlRoot}/create`
    }
    const key = params => {
      if (Object.keys(params ?? {}).length) {
        return `${this.urlRoot}/create?${stringify(params)}`
      }
      return `${this.urlRoot}/create`
    }
    const options = this.getFetchOptions()
    const endpoint = (params = {}, body) => this.fetch('post', `${this.urlRoot}/create`, body)
    endpoint.type = 'mutate'
    endpoint.schema = this
    endpoint.options = options
    endpoint.getFetchKey = getFetchKey
    endpoint.key = key
    endpoint.fetch = endpoint
    return endpoint
  }

  static detailShape() {
    const url = params => `${this.urlRoot}/get/${this.pk(params)}`
    const getFetchKey = params => url(params)
    const options = this.getFetchOptions()
    const endpoint = params => this.fetch('get', url(params))
    endpoint.type = 'read'
    endpoint.schema = this
    endpoint.options = options
    endpoint.getFetchKey = getFetchKey
    endpoint.key = params => url(params)
    endpoint.fetch = endpoint
    return endpoint
  }

  static updateShape() {
    const url = params => `${this.urlRoot}/edit/${this.pk(params)}`
    const getFetchKey = params => url(params)
    const options = this.getFetchOptions()
    const endpoint = (params, body) => this.fetch('put', url(params), body)
    endpoint.type = 'mutate'
    endpoint.schema = this
    endpoint.options = options
    endpoint.getFetchKey = getFetchKey
    endpoint.key = params => url(params)
    endpoint.fetch = endpoint
    return endpoint
  }

  static deleteShape() {
    const url = params => `${this.urlRoot}/delete/${this.pk(params)}`
    const getFetchKey = params => url(params)
    const options = this.getFetchOptions()
    const endpoint = params => this.fetch('delete', url(params))
    endpoint.type = 'mutate'
    endpoint.schema = this
    endpoint.options = options
    endpoint.getFetchKey = getFetchKey
    endpoint.key = params => url(params)
    endpoint.fetch = endpoint
    return endpoint
  }

  static countShape() {
    const url = () => `${this.urlRoot}/list`
    const getFetchKey = () => `${url()}/count`
    const options = this.getFetchOptions()
    const baseParams = {
      offset: 0,
      limit: 1,
      order: 'desc',
      sort: 'created_date'
    }
    const endpoint = (params = {}) => this.fetch('post', url(), { ...baseParams, ...params })
    endpoint.type = 'read'
    endpoint.schema = { total: 0, rows: [] }
    endpoint.options = options
    endpoint.getFetchKey = getFetchKey
    endpoint.key = () => `${url()}/count`
    endpoint.fetch = endpoint
    return endpoint
  }
}
